import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

// New interfaces for tab-specific analysis
export interface TabAnalysisRequest {
  brands: string[];
  category: string;
  industry: string;
  models: string[];
  attributes?: string[];
  tab_name: string;
}

export interface TabAnalysisResponse {
  tab_name: string;
  data: any;
  cached: boolean;
  timestamp: string;
  models_used: string[];
  structured_response?: StructuredTabResponse;
  validation_warnings?: ValidationStatus;
}

// New structured response interfaces
export interface StructuredTabResponse {
  tab_name: string;
  bar_chart: BarChartData;
  data_table: DataTableData;
  context: ContextSummary;
  timestamp: string;
  model_meta: ModelMeta;
  validation_status: ValidationStatus;
}

export interface BarChartData {
  title: string;
  chart_type: string;
  data: any[];
  caption: string;
  x_axis: string;
  y_axis: string;
  colors?: string[];
  citations?: CitationData[];
}

export interface DataTableData {
  title: string;
  headers: string[];
  rows: any[];
  citations: CitationData[];
  row_citations?: { [key: number]: CitationData[] };
}

export interface ContextSummary {
  title: string;
  summary: string;
  key_findings: string[];
  insights: string[];
  citations: CitationData[];
}

export interface CitationData {
  source: string;
  url: string;
  title: string;
  snippet: string;
  confidence: number;
  type?: string;
  domain?: string;
  category?: string; // Added for future use (e.g., 'rankings', 'research', 'news')
}

export interface ModelMeta {
  model_used: string;
  search_engines: string[];
  data_freshness: string;
  processing_time?: number;
  web_search_used?: boolean;
  citation_count?: number;
}

export interface ValidationStatus {
  has_bar_chart: boolean;
  has_data_table: boolean;
  has_context: boolean;
  has_citations: boolean;
  is_complete: boolean;
}

// Content Creation interfaces
export interface ContentCreationRequest {
  university_name: string;
  competitors?: string[];
  course: string;
  force_refresh?: boolean;
}

export interface ContentCreationResponse {
  university: string;
  content_status: ContentStatus;
  competitive_gaps: CompetitiveGap[];
  recommendations: ContentRecommendation[];
  timestamp: string;
  model_meta: ModelMeta;
}

export interface ContentStatus {
  university: string;
  content_types: ContentType[];
  total_content_pieces: number;
  content_score: number;
  last_updated: string;
}

export interface ContentType {
  type: string;
  count: number;
  quality_score: number;
  last_updated: string;
}

export interface CompetitiveGap {
  content_type: string;
  current_status: string;
  competitor_scores: { [key: string]: number };
  gap_percentage: number;
  priority: string;
  recommendations: string[];
}

export interface ContentRecommendation {
  content_type: string;
  piece_type: string;
  title: string;
  keywords: string[];
  primary_citation: CitationData;
  secondary_citation: CitationData;
  impact_score: number;
  estimated_effort: string;
}

// AI Visibility Index interfaces
export interface AIVisibilityRequest {
  university_name?: string; // For single university (legacy support)
  universities?: string[]; // For multiple universities (string array)
  course: string;
  include_competitors: boolean;
  force_refresh?: boolean;
}

export interface AIVisibilityResponse {
  university?: string; // For single university (legacy support)
  universities?: AIVisibilityUniversity[]; // For multiple universities with detailed data
  chart_data: AIVisibilityChartData;
  context: AIVisibilityContext;
  timestamp: string;
  model_meta: ModelMeta;
}

export interface AIVisibilityUniversity {
  name: string;
  ai_visibility_score: number;
  content_quality: number;
  digital_presence: number;
  innovation_index: number;
  ai_adoption_rate: number;
}

export interface AIVisibilityChartData {
  title: string;
  chart_type: string;
  data: AIVisibilityDataPoint[];
  x_axis: string;
  y_axis: string;
  caption: string;
  retrieval_timestamp: string;
  source_citations: CitationData[];
}

export interface AIVisibilityDataPoint {
  name: string;
  value: number;
  content_quality: number;
  digital_presence: number;
  innovation_index: number;
  ai_adoption_rate: number;
}

export interface AIVisibilityContext {
  title: string;
  explanation: string;
  key_metrics: string[];
  insights: string[];
  recommendations: string[];
  citations: CitationData[];
}

// AI Citations interfaces
export interface AICitationsRequest {
  university_name?: string; // For single university (legacy support)
  universities?: string[]; // For multiple universities (string array)
  course: string;
  country: string;
  include_competitors: boolean;
  force_refresh?: boolean;
}

export interface AICitationsResponse {
  university?: string; // For single university (legacy support)
  universities?: AICitationsUniversity[]; // For multiple universities with detailed data
  brands?: AICitationsUniversity[]; // For generic flow (multiple brands with detailed data)
  context: AICitationsContext;
  timestamp?: string;
  model_meta?: ModelMeta;
  /** Bulk/history payload may nest context under structured_data */
  structured_data?: { context?: AICitationsContext };
  llm_comparison?: unknown;
}

export interface AICitationsUniversity {
  name: string;
  citation_frequency: number;
  citation_share_of_voice: number;
  authority_weight: number;
  relevance_score: number;
  ai_readability: number;
  external_mentions: number;
  diversity_index: number;
  persistence_score: number;
}

export interface AICitationsChartData {
  title: string;
  chart_type: string;
  data: AICitationsDataPoint[];
  x_axis: string;
  y_axis: string;
  caption: string;
  retrieval_timestamp: string;
  source_citations: CitationData[];
}

export interface AICitationsDataPoint {
  name: string;
  series: string;
  value: number;
}

export interface CategoryAnalysisResponse {
  universities: any[];
  chart_data: any;
  context: CategoryAnalysisContext;
  timestamp: string;
  model_meta: any;
}

export interface CategoryAnalysisContext {
  analysis_type: string;
  total_universities: number;
  course: string;
  country: string;
  analysis_timestamp: string;
}

export interface CategoryAnalysisChartData {
  name: string;
  series: Array<{
    name: string;
    value: number;
  }>;
}

export interface CategoryAnalysisDataPoint {
  name: string;
  value: number;
  university?: string;
}

export interface AICitationsContext {
  title?: string;
  explanation?: string;
  key_metrics?: string[];
  insights?: string[];
  recommendations?: string[];
  citations?: CitationData[];
  /** Sources by domain (domain, category, authority_score) from bulk/overview flow */
  citations_table?: Array<{ domain: string; category?: string; authority_score?: number; monthly_traffic?: string }>;
}

export interface UnaidedAnalysisRequest {
  brands: string[];
  category: string;
  industry: string;
  models: string[];
  question_count?: number;
  question_templates?: string[];
}

export interface UnaidedAnalysisResponse {
  brand_mentions: { [key: string]: number };
  total_questions: number;
  brand_index_scores: { [key: string]: number };
  questions_asked: string[];
  timestamp: string;
}

export interface ProgressTrackingRequest {
  brand: string;
  category: string;
  industry: string;
  time_period?: string;
  models: string[];
}

export interface ProgressTrackingResponse {
  brand: string;
  category: string;
  current_score: number;
  previous_score: number;
  improvement: number;
  trend: string;
  data_points: any[];
  time_period: string;
}

// Education domain types
export type Role = 'student' | 'university';
export type Plan = 'free' | 'paid';

export interface EducationConfigResponse {
  courses: string[];
  countries: string[];
  default_models: string[];
  industry: string; // "Education"
}

export interface EducationQueryRequest {
  filters: {
    course?: string;
    country?: string | null; // 'Global' -> null
    models: string[]; // ['gemini']
    universities?: string[] | null;
    force_refresh?: boolean;
    experience_level?: string;
    budget_range?: string;
  };
  query: string;
}

export interface EducationQueryResponse {
  query: string;
  course: string;
  country: string; // 'Global' or country
  universities: string[];
  table: Array<{
    university: string;
    composite_score: number;
    sentiment: number;
    tuition_fees: string;
    employability: string;
    living_cost: string;
    visa_psw: string;
    enhanced_citations?: Array<{
      source: string;
      url: string;
      title: string;
      snippet: string;
      confidence: number;
      type: string;
      domain: string;
    }>;
    data_source?: string;
    last_verified?: string;
    source_badge?: string;
    live_badge?: string;
  }>;
  chart: {
    title: string;
    series: Array<{ name: string; value: number }>;
    x_axis: string;
    y_axis: string;
  };
  evidence_sources?: Array<{
    category: string;
    source: string;
    confidence: number;
    url?: string;
    snippet?: string;
    type?: string;
    domain?: string;
  }>;
  enhanced_facts?: any[];
  cached: boolean;
  data_source?: string;
  timestamp?: string;
  source_badge?: string;
  live_badge?: string;
  total_sources?: number;
  llm_sources?: number;
  bing_sources?: number;
}

export interface EducationTaskResponse {
  task_id: string;
  status: string;
  message?: string;
}

export interface EducationTaskStatusResponse {
  task_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  result?: EducationQueryResponse;
  error?: string;
  progress?: number;
}

// NEW: University comparison interfaces
export interface UniversityComparisonRequest {
  university: string;
  category: string;
  country: string;
  models: string[];
  force_refresh?: boolean;
}

export interface UniversityComparisonResponse {
  university: string;
  category: string;
  country: string;
  ranking: {
    position: number;
    total_compared: number;
    percentile: number;
    score: number;
    sentiment_score: number;
    performance_level: string;
  };
  comparison_data: Array<{
    university: string;
    score: number;
    is_target: boolean;
  }>;
  chart_data: {
    title: string;
    subtitle: string;
    series: Array<{
      name: string;
      value: number;
      is_target: boolean;
    }>;
    x_axis: string;
    y_axis: string;
  };
}

export interface IndividualUniversityRequest {
  university: string;
  category?: string;
  country?: string;
  models?: string[];
  force_refresh?: boolean;
}

export interface IndividualUniversityResponse {
  university: string;
  category: string;
  country: string;
  analysis: {
    overview: any;
    rankings: any;
    metrics: any;
    insights: any;
  };
  cached: boolean;
  timestamp: string;
}

export interface ContentSourceRequest {
  brand: string;
  category: string;
  industry: string;
  models: string[];
  include_urls?: boolean;
  include_influence_score?: boolean;
}

export interface ContentSourceResponse {
  brand: string;
  influential_sources: any[];
  citation_patterns: any[];
  influence_score: number;
  total_citations: number;
}

// ===== ENHANCED CONTENT ANALYSIS INTERFACES =====

export interface ContentAnalysisRequest {
  university_name: string;
}

export interface ContentAnalysisResponse {
  university_name: string;
  crawl_date: string;
  overall_score: number;
  content_scores: {
    website_content: number;
    youtube_content: number;
    social_media: number;
    digital_media: number;
    ugc: number;
    thought_leadership: number;
  };
  evidence_sources: Array<{
    category: string;
    source: string;
    url: string;
    timestamp: string;
    data_type: string;
    confidence_score: number;
  }>;
  recommendations: Array<{
    category: string;
    issue: string;
    recommendation: string;
    priority: string;
    priority_score: number;
    expected_impact: string;
    current_score: number;
    target_score: number;
  }>;
  tuition_fees: Array<{
    course_type: string;
    fee_amount: string;
    currency: string;
    duration: string;
    source_url: string;
    confidence_score: number;
  }>;
  raw_data: any;
}

export interface BatchContentAnalysisRequest {
  universities: string[];
}

export interface BatchContentAnalysisResponse {
  total_analyzed: number;
  successful: number;
  failed: number;
  results: Array<{
    university_name: string;
    overall_score: number;
    content_scores: {
      website_content: number;
      youtube_content: number;
      social_media: number;
      digital_media: number;
      ugc: number;
      thought_leadership: number;
    };
    tuition_fees_count: number;
    recommendations_count: number;
    evidence_count: number;
  }>;
}

export interface TuitionFeesSearchRequest {
  university_name: string;
  course_type?: string; // 'undergraduate', 'postgraduate', 'international', 'all'
}

export interface TuitionFeesSearchResponse {
  university_name: string;
  course_type: string;
  fees_count: number;
  fees: Array<{
    course_type: string;
    fee_amount: string;
    currency: string;
    duration: string;
    source_url: string;
    confidence_score: number;
  }>;
}

export interface EnhancedTabDataRequest {
  brands: string[];
  category?: string;
  industry?: string;
  models?: string[];
}

export interface EnhancedTabDataResponse {
  data: any[];
  chart_type: string;
  categories: string[];
  evidence_sources: Array<{
    category: string;
    source: string;
    url: string;
    confidence_score: number;
  }>;
  recommendations: Array<{
    category: string;
    issue: string;
    recommendation: string;
    priority: string;
    expected_impact: string;
  }>;
  overall_score?: number;
}

// ===== AI COURSE FINDER INTERFACES =====

export interface CourseSearchParams {
  query?: string;
  category?: string;
  country?: string;
  experience_level?: string;
  budget_range?: string;
  limit?: number;
}

export interface CourseData {
  university_name: string;
  country?: string;
  course?: string;
  course_name?: string;
  course_type?: string;
  duration?: string;
  ranking?: string;
  source?: string;
  tuition_fees?: string;
  living_costs?: string;
  employability_rate?: string;
  visa_support?: string;
  location?: string;
  university_type?: string;
  score?: number;
  fee_confidence?: number;
  fee_source?: string;
  evidence_sources?: Array<{ category: string; source: string; confidence: number; url?: string; }>;
  data_source?: string;
  last_verified?: string;
  ai_courses_offered: string[];
  ai_specializations: string[];
  career_outcomes: {
    employment_rate: number | string;
    average_salary: number | string;
    top_employers: string[];
    career_paths?: string[];
    job_roles?: string[];
  };
  student_satisfaction?: {
    overall_score: number;
    teaching_quality: number;
    facilities: number;
    support_services: number;
  };
  research_strength?: {
    ai_research_ranking: number;
    publications_count: number;
    research_grants: number;
    ai_labs: string[];
  };
}

export interface CourseSearchResponse {
  courses: CourseData[];
  search_summary: {
    total_courses_found: number;
    countries_represented: string[];
    fee_ranges: {
      lowest: string;
      highest: string;
      average: string;
    };
    experience_levels: string[];
    top_universities: string[];
  };
  context: {
    title: string;
    summary: string;
    methodology: string;
    insights: string[];
    citations: any[];
  };
  evidence_sources: any[];
  cached: boolean;
  timestamp: string;
  models_used: string[];
  plan_type: string;
  metadata: any;
}

export interface CourseComparisonParams {
  universities: string;
  course_type?: string;
}

export interface CourseComparisonData {
  basic_info: any;
  ai_courses: string[];
  specializations: string[];
  career_outcomes: any;
  student_satisfaction: any;
  research_strength: any;
}

export interface CourseComparisonResponse {
  comparison: { [key: string]: CourseComparisonData };
  comparison_summary: {
    universities_compared: number;
    course_type: string;
    overall_leader: {
      university: string;
      strength: string;
      score: number;
    };
    strength_analysis: {
      best_teaching: string;
      best_research: string;
      best_career_outcomes: string;
      best_value: string;
    };
    cost_comparison: {
      lowest_fees: string;
      highest_fees: string;
      average_fees: string;
    };
  };
  recommendations: any[];
  context: {
    title: string;
    summary: string;
    methodology: string;
    insights: string[];
    citations: any[];
  };
  evidence_sources: any[];
  cached: boolean;
  timestamp: string;
  models_used: string[];
  plan_type: string;
  metadata: any;
}

export interface CourseRecommendationParams {
  interests: string;
  experience_level: string;
  budget_range: string;
  preferred_location?: string;
  limit?: number;
}

export interface CourseRecommendationData extends CourseData {
  recommendation_score: number;
}

export interface CourseRecommendationResponse {
  recommendations: CourseRecommendationData[];
  recommendation_summary: {
    total_recommendations: number;
    interests: string;
    experience_level: string;
    budget_range: string;
    preferred_location: string;
    top_recommendation: {
      university: string;
      course: string;
      score: number;
      reason: string;
    };
    recommendation_breakdown: {
      by_experience_level: {
        beginner: number;
        intermediate: number;
        advanced: number;
      };
      by_budget: {
        low: number;
        medium: number;
        high: number;
      };
      by_location: {
        UK: number;
        USA: number;
        Other: number;
      };
    };
  };
  career_guidance: {
    career_paths: any[];
    skill_development: string[];
  };
  context: {
    title: string;
    summary: string;
    methodology: string;
    insights: string[];
    citations: any[];
  };
  evidence_sources: any[];
  cached: boolean;
  timestamp: string;
  models_used: string[];
  plan_type: string;
  metadata: any;
}

// ===== UNIVERSITY AI INDEX INTERFACES =====

export interface AIIndexMetrics {
  academic_reputation: number;
  research_strength: number;
  employment_outcomes: number;
  student_satisfaction: number;
  industry_connections: number;
  cost_effectiveness: number;
}

export interface AIIndexWeightedBreakdown {
  academic_reputation: number;
  research_strength: number;
  employment_outcomes: number;
  student_satisfaction: number;
  industry_connections: number;
  cost_effectiveness: number;
}

export interface AIIndexCalculationResponse {
  university_name: string;
  ai_index_score: number;
  metrics: AIIndexMetrics;
  weighted_breakdown: AIIndexWeightedBreakdown;
  rank: string;
}

export interface AIIndexRankingParams {
  country?: string;
  limit?: number;
}

export interface AIIndexRankingData extends AIIndexCalculationResponse {
  // Extends the calculation response
}

export interface AIIndexRankingsResponse {
  rankings: AIIndexRankingData[];
  total_rankings: number;
  country: string;
}

export interface AIIndexMetricInfo {
  weight: number;
  description: string;
}

export interface AIIndexMethodologyResponse {
  ai_index_metrics: {
    [key: string]: AIIndexMetricInfo;
  };
  total_weight: number;
  methodology: string;
}

@Injectable({
  providedIn: 'root'
})
export class BrandIndexService {
  private apiUrl = environment.apiUrl.endsWith('/') ? environment.apiUrl : environment.apiUrl + '/';
  static readonly BRAND_AI_FILTER_KEY = 'BRAND_AI_FILTERS';
  static readonly BRAND_AI_LOGIN_PIN_KEY = 'BRAND_AI_LOGIN_PIN';
  static readonly BRAND_AI_LOGIN_CORRECT_PIN = '8898';

  constructor(private http: HttpClient) { }

  getLocalStorageData() {
    let obj = localStorage.getItem(BrandIndexService.BRAND_AI_FILTER_KEY);
    if (obj) return JSON.parse(obj);
    return null;
  }

  setLocalStorageData(filter_key: string, value: any) {
    let obj = this.getLocalStorageData();
    if (obj) {
      obj[filter_key] = value;
      localStorage.setItem(BrandIndexService.BRAND_AI_FILTER_KEY, JSON.stringify(obj));
    } else {
      localStorage.setItem(BrandIndexService.BRAND_AI_FILTER_KEY, JSON.stringify({ [filter_key]: value }));
    }
  }

  removeLocalStorageData() {
    localStorage.removeItem(BrandIndexService.BRAND_AI_FILTER_KEY);
  }

  /* AUTHENTICATION starts */
  checkCorrectPin(pin: string) {
    return pin === BrandIndexService.BRAND_AI_LOGIN_CORRECT_PIN ? true : false;
  }

  isLoggedIn() {
    return localStorage.getItem(BrandIndexService.BRAND_AI_LOGIN_PIN_KEY) ? true : false;
  }

  loginUsingPin() {
    localStorage.setItem(BrandIndexService.BRAND_AI_LOGIN_PIN_KEY, 'true');
  }

  logout() {
    localStorage.removeItem(BrandIndexService.BRAND_AI_LOGIN_PIN_KEY);
  }
  /* AUTHENTICATION ends */

  // NEW: Tab-specific analysis methods
  getTabAnalysis(request: TabAnalysisRequest): Observable<TabAnalysisResponse> {
    return this.http.post<TabAnalysisResponse>(`${this.apiUrl}analysis/tab/${request.tab_name}`, request);
  }

  // NEW: Unaided analysis (Evertune-style)
  getUnaidedAnalysis(request: UnaidedAnalysisRequest): Observable<UnaidedAnalysisResponse> {
    return this.http.post<UnaidedAnalysisResponse>(`${this.apiUrl}unaided-analysis`, request);
  }

  // NEW: Progress tracking
  getBrandProgress(request: ProgressTrackingRequest): Observable<ProgressTrackingResponse> {
    return this.http.post<ProgressTrackingResponse>(`${this.apiUrl}brand-progress`, request);
  }

  // NEW: Content source analysis
  getContentSources(request: ContentSourceRequest): Observable<ContentSourceResponse> {
    return this.http.post<ContentSourceResponse>(`${this.apiUrl}content-sources`, request);
  }

  // EDUCATION: Config and query
  getEducationConfig(): Observable<EducationConfigResponse | any> {
    const url = `${this.apiUrl}education/config`;
    return this.http.get<any>(url).pipe(
      map((response: any) => {
        // Handle the actual API response format
        if (response.status === 'success' && response.data) {
          return response.data as EducationConfigResponse;
        }
        return response as EducationConfigResponse;
      })
    );
  }

  

  educationQuery(payload: EducationQueryRequest): Observable<EducationQueryResponse> {
    return this.http.post<EducationQueryResponse>(`${this.apiUrl}education/query-frontend`, payload);
  }

  // NEW: Education Overview Analysis endpoint
  getEducationOverviewAnalysis(payload: EducationQueryRequest): Observable<EducationQueryResponse> {
    return this.http.post<EducationQueryResponse>(`${this.apiUrl}education/overview/analysis`, payload);
  }

  educationQueryTask(payload: EducationQueryRequest): Observable<EducationTaskResponse> {
    return this.http.post<EducationTaskResponse>(`${this.apiUrl}education/query-task`, payload);
  }

  getEducationTaskStatus(taskId: string): Observable<EducationTaskStatusResponse> {
    return this.http.get<EducationTaskStatusResponse>(`${this.apiUrl}education/task-status/${taskId}`);
  }

  // NEW: University comparison methods with error handling
  getUniversityComparison(request: UniversityComparisonRequest): Observable<UniversityComparisonResponse> {
    return this.http.post<UniversityComparisonResponse>(`${this.apiUrl}education/compare-simple`, request)
      .pipe(
        this.handleRateLimitError<UniversityComparisonResponse>()
      );
  }

  getIndividualUniversity(universityName: string, request: IndividualUniversityRequest): Observable<IndividualUniversityResponse> {
    const encodedName = encodeURIComponent(universityName);
    return this.http.post<IndividualUniversityResponse>(`${this.apiUrl}education/university/${encodedName}`, request)
      .pipe(
        this.handleRateLimitError<IndividualUniversityResponse>()
      );
  }

  getAdvancedComparison(universityName: string, request: UniversityComparisonRequest): Observable<UniversityComparisonResponse> {
    const encodedName = encodeURIComponent(universityName);
    return this.http.post<UniversityComparisonResponse>(`${this.apiUrl}education/compare/${encodedName}`, request)
      .pipe(
        this.handleRateLimitError<UniversityComparisonResponse>()
      );
  }

  // NEW: Education helper endpoints
  getAvailableUniversities(country: string, category: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}education/universities?country=${country}&category=${category}`);
  }

  getAvailableCountries(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}education/universities/countries`);
  }

  getAvailableCategories(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}education/universities/categories`);
  }

  // Rate limiting error handler
  private handleRateLimitError<T>() {
    return (source: Observable<T>) => source.pipe(
      catchError((error: any) => {
        if (error.status === 429) {
          // Rate limit exceeded
          const retryAfter = error.headers?.get('Retry-After') || 60;
          const errorMessage = `Rate limit exceeded. Please try again in ${retryAfter} seconds.`;
          return throwError(() => ({
            error: {
              detail: errorMessage,
              retryAfter: parseInt(retryAfter)
            }
          }));
        }
        return throwError(() => error);
      })
    );
  }

  // Utility method for URL encoding university names
  private encodeUniversityName(name: string): string {
    return encodeURIComponent(name);
  }

  // Tab wrapper with education flag support
  getTab(tabName: string, payload: Omit<TabAnalysisRequest, 'tab_name'> & { force_refresh?: boolean }, isEducation: boolean = false): Observable<any> {
    const endpoint = isEducation ? 'education/tab' : 'analysis/tab';
    return this.http.post<any>(`${this.apiUrl}${endpoint}/${tabName}`, payload);
  }

  // NEW: Check system status
  checkSystemStatus(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}check-system-status`);
  }

  // NEW: Get API configuration
  getApiConfig(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}config`);
  }

  // ADMIN: Clear backend cache
  clearCache(): Observable<any> {
    return this.http.post(`${this.apiUrl}admin/clear-cache`, {});
  }

  // Legacy methods (keeping for backward compatibility)
  postSentimentAnalysis(payload: any) {
    return this.http.post<any>(`${this.apiUrl}analysis-sentiment`, payload);
  }

  postContentDistributionAnalysis(payload: any) {
    return this.http.post<any>(`${this.apiUrl}analysis-content-distribution`, payload);
  }

  postAreaComparisonAnalysis(payload: any) {
    return this.http.post<any>(`${this.apiUrl}analysis-area-comparison`, payload);
  }

  postAnalysisConsumerPreferences(payload: any) {
    return this.http.post<any>(`${this.apiUrl}analysis-consumer-preference`, payload);
  }

  postAnalysisMethodology(payload: any) {
    return this.http.post<any>(`${this.apiUrl}analysis-unique-responses`, payload);
  }

  postAnalysisBrandIndex(payload: any) {
    return this.http.post<any>(`${this.apiUrl}analysis-brand-index`, payload);
  }

  postAnalysisBrand(payload: any) {
    return this.http.post<any>(`${this.apiUrl}analysis-brand`, payload);
  }

  postAnalysis(payload: any) { // Not Working....
    return this.http.post<any>(`${this.apiUrl}analyze`, payload);
  }

  // V2 API methods
  postAnalysisV2(payload: any) {
    return this.http.post<any>(`${this.apiUrl}analyze`, payload);
  }

  getTaskResultV2(taskId: string) {
    return this.http.get<any>(`${this.apiUrl}task/${taskId}/result`);
  }

  getBrandScoreFullReport(payload: any) {
    return this.http.post<any>(`${this.apiUrl}get-brand-score-full-report`, payload);
  }

  getTaskStatus(taskId: string) {
    return this.http.get<any>(`${this.apiUrl}task-status/${taskId}`);
  }

  // New comprehensive analysis endpoint
  postComprehensiveAnalysis(payload: any) {
    return this.http.post<any>(`${this.apiUrl}create-comprehensive-task`, payload);
  }

  // Get task status for comprehensive analysis
  getComprehensiveTaskStatus(taskId: string) {
    return this.http.get<any>(`${this.apiUrl}task-status/${taskId}`);
  }

  // EEAT audit report
  postEEATAuditReport(payload: any) {
    return this.http.post<any>(`${this.apiUrl}content-authority-audit-llm`, payload);
  }

  // Task-based EEAT audit: initiate task
  postEEATAuditTask(payload: any) {
    return this.http.post<{ status: string; task_id: string; message: string; check_status_url: string }>(`${this.apiUrl}content-authority-audit-llm-task`, payload);
  }

  // Task-based EEAT audit: poll status/result
  getEEATAuditTaskStatus(taskId: string) {
    return this.http.get<any>(`${this.apiUrl}task-status/${taskId}`);
  }

  // Generic EEAT audit: initiate task
  postGenericEEATAudit(payload: any) {
    return this.http.post<{ status: string; task_id: string; message?: string; check_status_url?: string }>(`${this.apiUrl}generic/eeat-audit/analyze`, payload);
  }

  // Generic EEAT audit: poll status/result
  getGenericEEATAuditStatus(taskId: string) {
    return this.http.get<any>(`${this.apiUrl}generic/eeat-audit/status/${taskId}`);
  }

  // Content creation recommendations
  getContentCreationRecommendations(payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}content-creation/recommendations`, payload);
  }

  // ===== STRUCTURED RESPONSE METHODS =====

  // Get structured tab analysis with bar chart, table, and context
  getStructuredTabAnalysis(tabName: string, payload: TabAnalysisRequest): Observable<TabAnalysisResponse> {
    const endpoint = payload.industry === 'Education' ? 'education/tab' : 'analysis/tab';
    return this.http.post<TabAnalysisResponse>(`${this.apiUrl}${endpoint}/${tabName}`, payload)
      .pipe(
        catchError(error => {
          console.error('Structured tab analysis failed:', error);
          return throwError(() => error);
        })
      );
  }

  // ===== CONTENT CREATION SERVICE METHODS =====

  // Analyze content creation status and provide recommendations
  analyzeContentCreation(payload: ContentCreationRequest): Observable<ContentCreationResponse> {
    return this.http.post<ContentCreationResponse>(`${this.apiUrl}content-creation/analyze`, payload)
      .pipe(
        catchError(error => {
          console.error('Content creation analysis failed:', error);
          return throwError(() => error);
        })
      );
  }

  // Get content status for a university
  getContentStatus(universityName: string, course: string = 'MBA'): Observable<ContentStatus> {
    const encodedName = this.encodeUniversityName(universityName);
    return this.http.get<ContentStatus>(`${this.apiUrl}content-creation/status/${encodedName}?course=${course}`)
      .pipe(
        catchError(error => {
          console.error('Content status retrieval failed:', error);
          return throwError(() => error);
        })
      );
  }

  // Analyze competitive gaps in content
  analyzeCompetitiveGaps(payload: ContentCreationRequest): Observable<{ university: string; competitive_gaps: CompetitiveGap[] }> {
    return this.http.post<{ university: string; competitive_gaps: CompetitiveGap[] }>(`${this.apiUrl}content-creation/gaps/analyze`, payload)
      .pipe(
        catchError(error => {
          console.error('Competitive gap analysis failed:', error);
          return throwError(() => error);
        })
      );
  }

  // Generate content recommendations
  generateContentRecommendations(payload: ContentCreationRequest): Observable<{ university: string; recommendations: ContentRecommendation[] }> {
    return this.http.post<{ university: string; recommendations: ContentRecommendation[] }>(`${this.apiUrl}content-creation/recommendations/generate`, payload)
      .pipe(
        catchError(error => {
          console.error('Content recommendations generation failed:', error);
          return throwError(() => error);
        })
      );
  }

  // ===== AI VISIBILITY INDEX SERVICE METHODS =====

  // Analyze AI visibility index for a university
  analyzeAIVisibility(payload: AIVisibilityRequest): Observable<AIVisibilityResponse> {
    return this.http.post<AIVisibilityResponse>(`${this.apiUrl}education/ai-visibility/analyze`, payload)
      .pipe(
        catchError(error => {
          console.error('AI visibility analysis failed:', error);
          return throwError(() => error);
        })
      );
  }

  // ===== AI CITATIONS SERVICE METHODS =====

  // Analyze AI citations for universities
  analyzeAICitations(payload: AICitationsRequest): Observable<AICitationsResponse> {
    return this.http.post<AICitationsResponse>(`${this.apiUrl}education/ai-citations/analyze`, payload)
      .pipe(
        catchError(error => {
          console.error('AI citations analysis failed:', error);
          return throwError(() => error);
        })
      );
  }

  

  // Get AI visibility data for a university
  getAIVisibilityData(universityName: string, course: string = 'MBA'): Observable<any> {
    const encodedName = this.encodeUniversityName(universityName);
    return this.http.get<any>(`${this.apiUrl}ai-visibility/data/${encodedName}?course=${course}`)
      .pipe(
        catchError(error => {
          console.error('AI visibility data retrieval failed:', error);
          return throwError(() => error);
        })
      );
  }

  // Get AI visibility chart data
  getAIVisibilityChart(universityName: string, course: string = 'MBA', includeCompetitors: boolean = true): Observable<AIVisibilityChartData> {
    const encodedName = this.encodeUniversityName(universityName);
    return this.http.get<AIVisibilityChartData>(`${this.apiUrl}ai-visibility/chart/${encodedName}?course=${course}&include_competitors=${includeCompetitors}`)
      .pipe(
        catchError(error => {
          console.error('AI visibility chart generation failed:', error);
          return throwError(() => error);
        })
      );
  }

  // Get AI visibility context and explanation
  getAIVisibilityContext(universityName: string, course: string = 'MBA', includeCompetitors: boolean = true): Observable<AIVisibilityContext> {
    const encodedName = this.encodeUniversityName(universityName);
    return this.http.get<AIVisibilityContext>(`${this.apiUrl}ai-visibility/context/${encodedName}?course=${course}&include_competitors=${includeCompetitors}`)
      .pipe(
        catchError(error => {
          console.error('AI visibility context generation failed:', error);
          return throwError(() => error);
        })
      );
  }

  // Get competitor universities for AI visibility comparison
  getAIVisibilityCompetitors(universityName: string, course: string = 'MBA'): Observable<{ university: string; course: string; competitors: string[] }> {
    const encodedName = this.encodeUniversityName(universityName);
    return this.http.get<{ university: string; course: string; competitors: string[] }>(`${this.apiUrl}education/ai-visibility/competitors/${encodedName}?course=${course}`)
      .pipe(
        catchError(error => {
          console.error('Competitor retrieval failed:', error);
          return throwError(() => error);
        })
      );
  }

  // ===== EDUCATION TAB-SPECIFIC API METHODS =====

  // Get sentiment analysis data for education
  getEducationSentimentAnalysis(payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}education/sentiment-analysis`, payload)
      .pipe(
        catchError(error => {
          console.error('Education sentiment analysis failed:', error);
          return throwError(() => error);
        })
      );
  }

  // Get category analysis data for education
  getEducationCategoryAnalysis(payload: CategoryAnalysisRequest): Observable<CategoryAnalysisResponse> {
    // Use the new dedicated category analysis API
    return this.http.post<CategoryAnalysisResponse>(`${this.apiUrl}education/category-analysis`, payload)
      .pipe(
        catchError(error => {
          console.error('Education category analysis failed:', error);
          return throwError(() => error);
        })
      );
  }

  // Get area comparison data for education
  getEducationAreaComparison(payload: any): Observable<any> {
    // Use the new education area comparison endpoint
    return this.http.post<any>(`${this.apiUrl}education/area-comparison`, payload)
      .pipe(
        catchError(error => {
          console.error('Education area comparison failed:', error);
          return throwError(() => error);
        })
      );
  }

  // Get key influence sources data for education
  getEducationKeyInfluenceSources(payload: EducationQueryRequest): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}education/key-influence-sources`, payload)
      .pipe(
        catchError(error => {
          console.error('Education key influence sources failed:', error);
          return throwError(() => error);
        })
      );
  }

  // Get AI content index data for education
  getEducationAIContentIndex(payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}education/ai-content-index`, payload)
      .pipe(
        catchError(error => {
          console.error('Education AI content index failed:', error);
          return throwError(() => error);
        })
      );
  }

  // Get content creation data for education
  getEducationContentCreation(payload: any): Observable<any> {
    // Use the new enhanced content creation API instead of the old education endpoint
    const enhancedPayload = {
      university_name: payload.universities[0], // Take first university for now
      course: payload.course || 'MBA',
      country: payload.country || 'UK'
    };

    return this.http.post<any>(`${this.apiUrl}content-creation/analyze`, enhancedPayload)
      .pipe(
        catchError(error => {
          console.error('Enhanced content creation failed:', error);
          return throwError(() => error);
        })
      );
  }

  // Get performance tracking data for education
  getEducationPerformanceTracking(payload: EducationQueryRequest): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}education/performance-tracking`, payload)
      .pipe(
        catchError(error => {
          console.error('Education performance tracking failed:', error);
          return throwError(() => error);
        })
      );
  }

  // NEW: Our Methodology API (following AI Visibility Index pattern)
  getOurMethodologyAnalysis(payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}education/our-methodology/analyze`, payload)
      .pipe(
        this.handleRateLimitError<any>()
      );
  }

  // NEW: Performance Tracking API (following AI Visibility Index pattern)
  getPerformanceTrackingAnalysis(payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}education/performance-tracking/analyze`, payload)
      .pipe(
        this.handleRateLimitError<any>()
      );
  }

  // NEW: Key Influence Sources API (following AI Visibility Index pattern)
  getKeyInfluenceSourcesAnalysis(payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}education/key-influence-sources/analyze`, payload)
      .pipe(
        this.handleRateLimitError<any>()
      );
  }

  // NEW: Enhanced Consumer Insights API (following AI Visibility Index pattern)
  getConsumerInsightsAnalysis(payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}education/consumer-insights/enhanced-analyze`, payload)
      .pipe(
        this.handleRateLimitError<any>()
      );
  }

  // Get student comparison data for education
  getEducationStudentComparison(payload: EducationQueryRequest): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}education/student-comparison`, payload)
      .pipe(
        catchError(error => {
          console.error('Education student comparison failed:', error);
          return throwError(() => error);
        })
      );
  }

  // ===== AI COURSE FINDER SERVICE METHODS =====

  // Search AI courses
  searchAICourses(params: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}education/ai-courses/search`, params)
      .pipe(
        this.handleRateLimitError<any>()
      );
  }

  // Compare AI courses
  compareAICourses(params: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}education/ai-courses/compare`, params)
      .pipe(
        this.handleRateLimitError<any>()
      );
  }

  // Get personalized recommendations
  getAICourseRecommendations(params: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}education/ai-courses/recommendations`, params)
      .pipe(
        this.handleRateLimitError<any>()
      );
  }

  // ===== UNIVERSITY AI INDEX SERVICE METHODS =====

  // ===== ENHANCED CONTENT ANALYSIS SERVICE METHODS =====

  // Analyze single university content comprehensively
  analyzeUniversityContent(request: ContentAnalysisRequest): Observable<ContentAnalysisResponse> {
    return this.http.post<ContentAnalysisResponse>(`${this.apiUrl}content-analysis/university`, request)
      .pipe(
        this.handleRateLimitError<ContentAnalysisResponse>()
      );
  }

  // Analyze multiple universities in batch
  analyzeMultipleUniversities(request: BatchContentAnalysisRequest): Observable<BatchContentAnalysisResponse> {
    return this.http.post<BatchContentAnalysisResponse>(`${this.apiUrl}content-analysis/batch`, request)
      .pipe(
        this.handleRateLimitError<BatchContentAnalysisResponse>()
      );
  }

  // Get cached analysis for a university
  getUniversityAnalysis(universityName: string): Observable<ContentAnalysisResponse> {
    return this.http.get<ContentAnalysisResponse>(`${this.apiUrl}content-analysis/${universityName}`)
      .pipe(
        this.handleRateLimitError<ContentAnalysisResponse>()
      );
  }

  // Search tuition fees by university and course type
  searchTuitionFees(request: TuitionFeesSearchRequest): Observable<TuitionFeesSearchResponse> {
    return this.http.post<TuitionFeesSearchResponse>(`${this.apiUrl}tuition-fees/search`, request)
      .pipe(
        this.handleRateLimitError<TuitionFeesSearchResponse>()
      );
  }

  // Get enhanced tab data with content analysis
  getEnhancedTabData(tabName: string, request: EnhancedTabDataRequest): Observable<EnhancedTabDataResponse> {
    return this.http.post<EnhancedTabDataResponse>(`${this.apiUrl}education/enhanced-tab/${tabName}`, request)
      .pipe(
        this.handleRateLimitError<EnhancedTabDataResponse>()
      );
  }

  // Calculate AI Index for a university
  calculateAIIndex(universityName: string): Observable<any> {
    const encodedName = encodeURIComponent(universityName);
    return this.http.get<any>(`${this.apiUrl}ai-index/calculate/${encodedName}`)
      .pipe(
        this.handleRateLimitError<any>()
      );
  }

  // Get AI Index rankings
  getAIIndexRankings(params: any = {}): Observable<any> {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value.toString());
      }
    });

    return this.http.get<any>(`${this.apiUrl}ai-index/rankings?${queryParams.toString()}`)
      .pipe(
        this.handleRateLimitError<any>()
      );
  }

  // Get AI Index methodology
  getAIIndexMethodology(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}ai-index/metrics`)
      .pipe(
        this.handleRateLimitError<any>()
      );
  }

  // ===== SIMPLIFIED EDUCATION ENDPOINTS =====

  // Simplified search without category dropdown
  searchEducationCourses(params: any): Observable<any> {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value.toString());
      }
    });

    return this.http.get<any>(`${this.apiUrl}education/search?${queryParams.toString()}`)
      .pipe(
        this.handleRateLimitError<any>()
      );
  }

  // Simplified area comparison with reduced metrics
  getSimplifiedAreaComparison(payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}education/simplified-area-comparison`, payload)
      .pipe(
        this.handleRateLimitError<any>()
      );
  }

  // Get student information categories
  getStudentInfoCategories(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}education/student-info-categories`)
      .pipe(
        this.handleRateLimitError<any>()
      );
  }

  // Streaming Analysis methods
  startStreamingAnalysis(request: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}api/v1/streaming/ai-visibility-analysis`, request)
      .pipe(
        this.handleRateLimitError<any>()
      );
  }

  getStreamingStatus(taskId: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}api/v1/streaming/status/${taskId}`)
      .pipe(
        this.handleRateLimitError<any>()
      );
  }

  getStreamingResults(taskId: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}api/v1/streaming/results/${taskId}`)
      .pipe(
        this.handleRateLimitError<any>()
      );
  }

  generateDetailedContent(request: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}api/v1/streaming/generate-content`, request)
      .pipe(
        this.handleRateLimitError<any>()
      );
  }

  getBaseUrl(): string {
    return this.apiUrl;
  }
}

// ===== CATEGORY ANALYSIS API INTERFACES =====

export interface CategoryAnalysisRequest {
  universities: string[];
  categories?: string[];
  country?: string;
}

export interface UniversityData {
  name: string;
  score: number;
  rankings: {
    world_ranking: number;
    category_ranking: number;
    research_ranking: number;
  };
  employment_data: {
    employment_rate: number;
    average_salary: string;
    top_employers: string[];
  };
  course_offerings: string[];
  fees: {
    domestic: number;
    international: number;
  };
  strengths: string[];
  weaknesses: string[];
}

export interface CategoryMetrics {
  average_score: number;
  top_performer: string;
  score_range: string;
  employment_average: number;
  fee_average: number;
}

export interface CategoryAnalysis {
  category: string;
  universities: UniversityData[];
  category_metrics: CategoryMetrics;
}

